import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { View, StyleSheet, Animated } from "react-native";
import { XIcon } from "@phosphor-icons/react";

import CustomText from "../../../common/general/CustomText/CustomText";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import CustomModal from "../../../common/general/CustomModal/CustomModal";
import AssignmentGradingPanel from "./AssignmentGradingPanel";
import AssessmentGradingPanel from "./AssessmentGradingPanel";
import ProjectGradingPanel from "./ProjectGradingPanel";
import { GradingCenterModalState } from "../types";
import { useGradingCenterSubmissions } from "../../../../contexts/GradingCenterContext";
import {
  GetAssessmentSubmissionsDetailsDocument,
  GetAssessmentSubmissionAttemptHistoryDocument,
  GetAssessmentSubmissionsForGradingDocument,
  GetGradingCenterDashboardDataDocument,
  UpdatedEntityType,
  UpdateAssessmentSubmissionUpdatedGrade,
  useUpdateAssessmentSubmissionGradeMutation,
  useUpdateStudentAssignmentGradeMutation,
  useGradeProjectSubmissionMutation,
  UpdateStudentAssignmentGradeInput,
  GetAssignmentAndSubmissionBySubmissionIdDocument,
  GetProjectSubmissionDocument,
  UnifiedSubmission,
  ProjectRubricScoreInput,
} from "@axio/graphql/generated/graphql";
import { useAuth } from "../../../../hooks/useAuth";
import { useTranslation } from "react-i18next";
import { useNavigation, useRoute } from "@react-navigation/native";
import type { StackNavigationProp } from "@react-navigation/stack";
import type { RootStackParamList } from "../../../../navigation/navigation.types";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";
import { useToast } from "../../../../contexts/Toast/ToastProvider";

type AdminGradingCenterNavProp = StackNavigationProp<RootStackParamList, "Admin Grading Center">;

interface GradingPanelProps {
  modalState: GradingCenterModalState;
  setModalState: (modalState: GradingCenterModalState) => void;
  setUpdateError: (updateError: string | null) => void;
  setActionResult: (actionResult: string) => void;
  enablePublishGradeConfirmation?: boolean;
  onPublishGrade?: (submissionData: UnifiedSubmission) => Promise<{ success: boolean; message: string }>;
}

const CloseIcon = XIcon as unknown as React.ComponentType<{ size?: number; color?: string }>;

const GradingPanel: React.FC<GradingPanelProps> = ({
  modalState,
  setModalState,
  setUpdateError,
  setActionResult,
  enablePublishGradeConfirmation = false,
  onPublishGrade,
}) => {
  const [slideAnim] = useState(new Animated.Value(0));
  const navigation = useNavigation<AdminGradingCenterNavProp>();
  const route = useRoute();
  // Present only on the LTI assessment grading route; absent in the admin
  // grading center (where refetchUnifiedSubmissionsData handles the refresh).
  const assessmentId = (route.params as { assessmentId?: string } | undefined)?.assessmentId;

  const { userInfo } = useAuth();
  const { showToast } = useToast();
  const { t } = useTranslation();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [isPublishConfirmModalVisible, setIsPublishConfirmModalVisible] = useState(false);
  const [isPublishAvailableAfterSave, setIsPublishAvailableAfterSave] = useState(false);
  const actionResultTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    selectedSubmissionData,
    setSelectedSubmissionData,
    refetchUnifiedSubmissionsData,
    updatedAssessmentGradeEntry,
    updatedAssessmentRetakePayload,
    updatedAssignmentPayload,
    updatedProjectPayload,
    setUpdatedAssessmentGradeEntry,
    setUpdatedAssessmentRetakePayload,
    setUpdatedAssignmentPayload,
    setUpdatedProjectPayload,
    canSaveAssignment,
    canSaveAssessment,
    canSaveProject,
  } = useGradingCenterSubmissions();

  const handleCloseModal = () => {
    setModalState(GradingCenterModalState.CLOSED);
    setSelectedSubmissionData(null);
    navigation.setParams({ submissionId: undefined });
  };

  useEffect(() => {
    setValidationError("");
    if (modalState !== GradingCenterModalState.CLOSED) {
      Animated.timing(slideAnim, {
        toValue: 1,
        duration: 300,
        useNativeDriver: false,
      }).start();
    } else {
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: false,
      }).start();
    }
  }, [modalState, slideAnim]);

  useEffect(() => {
    return () => {
      if (actionResultTimeoutRef.current) {
        clearTimeout(actionResultTimeoutRef.current);
        actionResultTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (modalState === GradingCenterModalState.CLOSED) {
      setIsPublishAvailableAfterSave(false);
      return;
    }

    if (modalState !== GradingCenterModalState.VIEW_ASSESSMENT_RESPONSE) {
      setIsPublishAvailableAfterSave(false);
    }
  }, [modalState, selectedSubmissionData?.id]);

  const handleBeforeUpdate = () => {
    setUpdateError(null);
    setIsSubmitting(true);
  };

  const handleAfterUpdate = (actionResultMessage: string, closeModal: boolean) => {
    setIsSubmitting(false);
    setActionResult(actionResultMessage);
    if (actionResultTimeoutRef.current) {
      clearTimeout(actionResultTimeoutRef.current);
    }

    actionResultTimeoutRef.current = setTimeout(() => {
      setActionResult("");
      actionResultTimeoutRef.current = null;
    }, 3000);
    setUpdatedAssessmentGradeEntry(null);
    setUpdatedAssessmentRetakePayload(null);
    setUpdatedAssignmentPayload(null);
    setUpdatedProjectPayload(null);
    if (closeModal) {
      setModalState(GradingCenterModalState.CLOSED);
      setSelectedSubmissionData(null);
      navigation.setParams({ submissionId: undefined });
    }
  };

  const handleOnError = (errorMessage: string) => {
    setUpdateError(errorMessage);
    setIsSubmitting(false);
  };

  const isSelectedSubmissionNeedsReview =
    selectedSubmissionData?.status === "NEEDS_REVIEW" || selectedSubmissionData?.status === "Needs Review";

  // ASSIGNMENT UPDATE
  const [updateStudentAssignmentGrade] = useUpdateStudentAssignmentGradeMutation({
    refetchQueries: [
      {
        query: GetGradingCenterDashboardDataDocument,
        variables: {
          facultyId: userInfo.userId,
        },
      },
      {
        query: GetAssignmentAndSubmissionBySubmissionIdDocument,
        variables: {
          submissionId: selectedSubmissionData?.id || "",
        },
      },
      "GetGradebook",
    ],
  });

  const handleSaveAssignmentGrade = async (
    submissionData: UnifiedSubmission,
    updatePayload: UpdateStudentAssignmentGradeInput
  ) => {
    if (updatePayload.grade === null || updatePayload.grade === undefined) {
      return;
    }

    handleBeforeUpdate();
    try {
      await updateStudentAssignmentGrade({
        variables: {
          submissionId: submissionData.id,
          updatePayload,
        },
      });

      await refetchUnifiedSubmissionsData();

      handleAfterUpdate(`Grade saved for ${submissionData.student?.name}`, true);
    } catch (error) {
      if (error?.message?.includes("Evaluation is currently pending")) {
        handleOnError(error.message);
      } else {
        handleOnError(error.message || "Failed to save grade");
      }
    }
  };

  // --- END OF ASSIGNMENT UPDATE ---

  // DISABLE SAVE LOGIC

  const disableSave =
    isSubmitting ||
    (modalState === GradingCenterModalState.VIEW_ASSIGNMENT_RESPONSE && !canSaveAssignment) ||
    (modalState === GradingCenterModalState.VIEW_ASSESSMENT_RESPONSE && !canSaveAssessment) ||
    (modalState === GradingCenterModalState.VIEW_PROJECT_RESPONSE && !canSaveProject);

  const shouldShowPublishGradeButton =
    enablePublishGradeConfirmation &&
    modalState === GradingCenterModalState.VIEW_ASSESSMENT_RESPONSE &&
    isPublishAvailableAfterSave;
  const disablePublishGrade = isSubmitting || !selectedSubmissionData;

  // END OF DISABLE SAVE LOGIC

  // ASSESSMENT UPDATE

  const [updateAssessmentSubmissionGrade] = useUpdateAssessmentSubmissionGradeMutation({
    refetchQueries: [
      {
        query: GetGradingCenterDashboardDataDocument,
        variables: {
          facultyId: userInfo.userId,
        },
      },
      {
        query: GetAssessmentSubmissionsDetailsDocument,
        variables: {
          assessmentSubmissionId: selectedSubmissionData?.id || "",
        },
      },
      {
        query: GetAssessmentSubmissionAttemptHistoryDocument,
        variables: {
          assessmentSubmissionId: selectedSubmissionData?.id || "",
        },
      },
      "GetGradebook",
      // Refresh the LTI assessment grading table so the edited grade is
      // reflected without a manual reload. Only active on the LTI grading route.
      ...(assessmentId
        ? [
            {
              query: GetAssessmentSubmissionsForGradingDocument,
              variables: { assessmentId },
            },
          ]
        : []),
    ],
  });

  const handleSaveAssessmentGrade = async (
    submissionData: UnifiedSubmission,
    updatedGradeEntry: UpdateAssessmentSubmissionUpdatedGrade,
    closeModal: boolean,
    reviewWithoutAdjustment = false
  ) => {
    handleBeforeUpdate();

    try {
      const retakeUpdate = updatedAssessmentRetakePayload
        ? {
            retakeAllowed: updatedAssessmentRetakePayload.retakeAllowed,
            retakeDeadline: updatedAssessmentRetakePayload.retakeAllowed
              ? updatedAssessmentRetakePayload.retakeDeadline
              : null,
          }
        : {};

      await updateAssessmentSubmissionGrade({
        variables: {
          assessmentSubmissionId: submissionData.id,
          updatedGradeEntry: {
            ...updatedGradeEntry,
            ...retakeUpdate,
          },
          reviewWithoutAdjustment,
        },
      });

      await refetchUnifiedSubmissionsData();

      if (
        enablePublishGradeConfirmation &&
        modalState === GradingCenterModalState.VIEW_ASSESSMENT_RESPONSE &&
        !closeModal
      ) {
        setIsPublishAvailableAfterSave(true);
      }

      handleAfterUpdate(`Grade saved for ${submissionData.student?.name}`, closeModal);
    } catch (error) {
      if (error?.message?.includes("Evaluation is currently pending")) {
        handleOnError(error.message);
      } else {
        handleOnError(error.message || "Failed to save grade");
      }
    }
  };

  const handleSave = () => {
    if (modalState === GradingCenterModalState.VIEW_ASSESSMENT_RESPONSE) {
      const shouldCloseModalAfterSave = !enablePublishGradeConfirmation;

      const isRetakeDeadlineExpired =
        updatedAssessmentRetakePayload?.retakeAllowed === true &&
        !!updatedAssessmentRetakePayload?.retakeDeadline &&
        new Date(updatedAssessmentRetakePayload.retakeDeadline).getTime() <= Date.now();

      if (isRetakeDeadlineExpired) {
        showToast({
          message: "Retake deadline must be in the future",
          type: "error",
        });
        return;
      }

      if (!updatedAssessmentGradeEntry?.updatedEntityId) {
        if (updatedAssessmentRetakePayload) {
          handleSaveAssessmentGrade(
            selectedSubmissionData,
            {
              updatedEntityType: UpdatedEntityType.Submission,
              updatedEntityId: selectedSubmissionData.id,
              updatedGrade: String(selectedSubmissionData.grade ?? 0),
            },
            shouldCloseModalAfterSave,
            true
          );
          return;
        }

        if (!isSelectedSubmissionNeedsReview) {
          handleCloseModal();
          return;
        }

        handleSaveAssessmentGrade(
          selectedSubmissionData,
          {
            updatedEntityType: UpdatedEntityType.Submission,
            updatedEntityId: selectedSubmissionData.id,
            updatedGrade: String(selectedSubmissionData.grade ?? 0),
          },
          shouldCloseModalAfterSave,
          true
        );
        return;
      }
      if (updatedAssessmentGradeEntry) {
        handleSaveAssessmentGrade(
          selectedSubmissionData,
          updatedAssessmentGradeEntry,
          shouldCloseModalAfterSave,
          false
        );
      }
    }

    if (modalState === GradingCenterModalState.VIEW_ASSIGNMENT_RESPONSE) {
      if (!updatedAssignmentPayload) {
        setModalState(GradingCenterModalState.CLOSED);
        return;
      }
      // Validate required fields
      if (updatedAssignmentPayload.grade === null || updatedAssignmentPayload.grade === undefined) {
        showToast({
          message: t("text.grade_required"),
          type: "error",
        });
        return;
      }

      handleSaveAssignmentGrade(selectedSubmissionData, updatedAssignmentPayload);
    }

    if (modalState === GradingCenterModalState.VIEW_PROJECT_RESPONSE) {
      if (!updatedProjectPayload) {
        setModalState(GradingCenterModalState.CLOSED);
        return;
      }
      handleSaveProjectGrade(selectedSubmissionData, updatedProjectPayload);
    }
  };

  const handlePublishGrade = () => {
    setIsPublishConfirmModalVisible(false);

    if (!onPublishGrade || !selectedSubmissionData) {
      showToast({
        message: "Publish action is unavailable.",
        type: "error",
      });
      return;
    }

    handleBeforeUpdate();

    void onPublishGrade(selectedSubmissionData)
      .then((result) => {
        if (result.success) {
          handleAfterUpdate(result.message, true);
          return;
        }

        handleOnError(result.message);
      })
      .catch((error) => {
        handleOnError(error?.message || "Failed to publish grade");
      });
  };

  // END OF ASSESSMENT UPDATE

  // PROJECT UPDATE
  const [gradeProjectSubmission] = useGradeProjectSubmissionMutation({
    refetchQueries: [
      {
        query: GetGradingCenterDashboardDataDocument,
        variables: {
          facultyId: userInfo.userId || "",
        },
      },
      {
        query: GetProjectSubmissionDocument,
        variables: {
          id: selectedSubmissionData?.id || "",
        },
      },
      "GetGradebook",
    ],
  });

  const handleSaveProjectGrade = async (
    submissionData: UnifiedSubmission,
    gradePayload: { grade: number | null; feedback: string; rubricScores?: ProjectRubricScoreInput[] }
  ) => {
    if (gradePayload.grade === null || gradePayload.grade === undefined) {
      return;
    }

    handleBeforeUpdate();
    try {
      await gradeProjectSubmission({
        variables: {
          submissionId: submissionData.id,
          grade: gradePayload.grade,
          feedback: gradePayload.feedback || undefined,
          rubricScores: gradePayload.rubricScores || undefined,
        },
      });

      await refetchUnifiedSubmissionsData();

      handleAfterUpdate(`Grade saved for ${submissionData.student?.name}`, true);
    } catch (error) {
      handleOnError(error.message || "Failed to save grade");
    }
  };

  // END OF PROJECT UPDATE

  const panelWidth = 800;
  const translateX = slideAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [panelWidth, 0],
  });

  const panelContent = (
    <>
      {/* Dark overlay */}
      <Animated.View
        style={[
          styles.overlay,
          {
            opacity: slideAnim,
          },
        ]}
        onTouchEnd={handleCloseModal}
      />

      {/* Grading panel */}
      <Animated.View
        style={[
          styles.panel,
          {
            transform: [{ translateX }],
          },
        ]}>
        {/* Header */}
        <View style={styles.header}>
          <CustomText
            text={
              modalState === GradingCenterModalState.VIEW_ASSESSMENT_RESPONSE
                ? "Grade Assessment"
                : modalState === GradingCenterModalState.VIEW_PROJECT_RESPONSE
                  ? "Grade Project"
                  : "Grade Assignment"
            }
            size="l"
            weight="bold"
          />
          <CustomButton
            onPress={handleCloseModal}
            styleType="transparent"
            style={styles.closeButton}
            leftIcon={<CloseIcon size={24} color="#6B7280" />}
            rightIcon={null}
          />
        </View>
        {/* Conditional Panel Content */}
        {modalState === GradingCenterModalState.VIEW_ASSESSMENT_RESPONSE && (
          <AssessmentGradingPanel setModalState={setModalState} />
        )}
        {modalState === GradingCenterModalState.VIEW_ASSIGNMENT_RESPONSE && (
          <AssignmentGradingPanel setModalState={setModalState} />
        )}
        {modalState === GradingCenterModalState.VIEW_PROJECT_RESPONSE && (
          <ProjectGradingPanel setModalState={setModalState} />
        )}
        {modalState === GradingCenterModalState.VIEW_ASSIGNMENT_RESPONSE && validationError && (
          <>
            <CustomSpacing type="vertical" size="s" />
            <CustomText text={validationError} size="s" style={styles.errorText} useTranslationText={false} />
            <CustomSpacing type="vertical" size="xl" />
          </>
        )}
        {/* Footer */}
        <View style={styles.footer}>
          <CustomButton
            text="Cancel"
            size="s"
            styleType="lightStroke"
            disabled={isSubmitting}
            onPress={handleCloseModal}
            leftIcon={null}
            rightIcon={null}
          />
          <View style={styles.footerRightActions}>
            {shouldShowPublishGradeButton && (
              <CustomButton
                text={t("text.publish_grade")}
                useTranslationText={false}
                size="s"
                styleType="lightStroke"
                disabled={disablePublishGrade}
                style={disablePublishGrade ? styles.disabledButton : {}}
                textStyle={disablePublishGrade ? styles.disabledText : {}}
                onPress={() => setIsPublishConfirmModalVisible(true)}
                leftIcon={null}
                rightIcon={null}
              />
            )}
            <CustomButton
              text="Save Grade"
              size="s"
              styleType="primary"
              disabled={disableSave}
              style={disableSave ? styles.disabledButton : {}}
              textStyle={disableSave ? styles.disabledText : {}}
              onPress={handleSave}
              leftIcon={null}
              rightIcon={null}
            />
          </View>
        </View>

        <CustomModal
          visible={isPublishConfirmModalVisible}
          onRequestClose={() => setIsPublishConfirmModalVisible(false)}
          title={t("text.confirm_grade_publishing_title")}
          useTranslationText={false}
          showCloseButton>
          <View style={styles.confirmationBody}>
            <CustomText text={t("text.publish_grade_warning_message")} size="s" useTranslationText={false} />
            <View style={styles.confirmationActions}>
              <CustomButton
                text={t("text.publish_grade_cancel")}
                useTranslationText={false}
                styleType="primaryTransparentStroke"
                onPress={() => setIsPublishConfirmModalVisible(false)}
                disabled={isSubmitting}
              />
              <CustomButton
                text={t("text.publish_grade_confirm")}
                useTranslationText={false}
                styleType="primary"
                onPress={handlePublishGrade}
                disabled={isSubmitting}
              />
            </View>
          </View>
        </CustomModal>
      </Animated.View>
    </>
  );

  if (modalState === GradingCenterModalState.CLOSED) return null;

  // Render to document body to ensure it's at the root level
  return createPortal(panelContent, document.body);
};

const styles = StyleSheet.create({
  disabledButton: {
    backgroundColor: "#E5E7EB",
    borderColor: "#D1D5DB",
    shadowColor: "transparent",
  },
  disabledText: {
    color: "#9CA3AF",
  },
  errorText: {
    color: "#dc2626",
    fontSize: 16,
    fontWeight: "400",
  },
  overlay: {
    // @ts-expect-error - works for web
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 99,
  },
  panel: {
    // @ts-expect-error - works for web
    position: "fixed",
    top: 0,
    right: 0,
    bottom: 0,
    width: 800,
    backgroundColor: "#FFFFFF",
    borderLeftWidth: 1,
    borderLeftColor: "#E5E7EB",
    shadowColor: "#000",
    shadowOffset: {
      width: -2,
      height: 0,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 5,
    zIndex: 100,
    // @ts-expect-error - works for web
    height: "100vh",
    flexDirection: "column",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 24,
    paddingVertical: 20,
    backgroundColor: "#F9FAFB",
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
  },
  closeButton: {
    padding: 8,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 12,
    padding: 24,
    backgroundColor: "#F9FAFB",
    borderTopWidth: 1,
    borderTopColor: "#EAECF0",
  },
  footerRightActions: {
    flexDirection: "row",
    gap: 12,
  },
  confirmationBody: {
    padding: 24,
  },
  confirmationActions: {
    marginTop: 20,
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 12,
  },
});

export default GradingPanel;
